import { ExtendedError } from './ExtendedError'
import type { UnknownRecord } from './types'
import { removeEmptyValues, serializeObject } from './utils/serializeObject'

/**
 * @class IncenseError - the bespoke `Incense` wrapper that captures rich error history
 *
 * Extends `ExtendedError` while preserving a timeline (`errorHistory`) of previous
 * exceptions when an error is re-thrown through Incense’s `rethrow()` helper
 *
 * @public {UnknownRecord[]} errorHistory - running list of serialized prior errors
 */
export class IncenseError extends ExtendedError {
  errorHistory?: UnknownRecord[]

  /**
   * Creates an `IncenseError` instance
   *
   * @param {ExtendedError} errorDetails - Destructured `{ name, message, statusCode }` for an `ExtendedError`
   * @param {unknown} [previousError] - Previously thrown error being chained (can be raw `Error`, `IncenseError`, or any value)
   */
  constructor({ name, message, statusCode }: ExtendedError, previousError?: unknown) {
    super(name, message, statusCode)

    this.buildErrorHistory(previousError)

    if (Error.captureStackTrace && typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, ExtendedError)
    }
  }

  /**
   * @private buildErrorHistory
   *
   * Serializes and appends the supplied `previousException` to `errorHistory`,
   * normalising prototypal `Error` objects into plain objects and trimming
   * unnecessary props like `stack` for brevity
   *
   * The algorithm loosely follows:
   * 1. Determine if the incoming exception is an object
   * 2. Determine if the incoming exception is itself an `IncenseError`
   * 3. Initialise `errorHistory` with any existing history
   * 4. Clean object exceptions via `removeEmptyValues` + `serializeObject`
   * 5. Strip `stack` traces from serialised objects to avoid noise
   * 6. Push the cleaned exception onto `errorHistory`
   *
   * @param {unknown} [previousException] - The exception caught higher in the stack
   */
  private buildErrorHistory(previousException?: unknown): void {
    if (!previousException) {
      return
    }

    const previousExceptionIsObject = this.isUnknownRecord(previousException)
    const previousExceptionIsIncense = this.isIncenseError(previousException)

    this.errorHistory = previousExceptionIsIncense ? previousException.errorHistory : []

    const cleanedPreviousException = previousExceptionIsObject
      ? removeEmptyValues(serializeObject(previousException))
      : previousException

    if (this.isUnknownRecord(cleanedPreviousException)) {
      delete cleanedPreviousException['stack']
    }

    if (previousExceptionIsIncense) {
      delete previousException.errorHistory
    }

    this.errorHistory?.push(cleanedPreviousException as UnknownRecord)
  }

  private isUnknownRecord(value: unknown): value is UnknownRecord {
    return typeof value === 'object' && value !== null
  }

  private isIncenseError(value: unknown): value is IncenseError {
    return value instanceof IncenseError
  }
}
